import React, { useEffect, useRef, useState } from 'react';

// ---------- formatting helpers (Indian rupee conventions) ----------
export const formatINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export const formatINRCompact = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const trim = (x) => String(Math.round(x * 100) / 100);
  if (abs >= 1e7) return `₹${trim(v / 1e7)} Cr`;
  if (abs >= 1e5) return `₹${trim(v / 1e5)} L`;
  if (abs >= 1e3) return `₹${trim(v / 1e3)}K`;
  return `₹${Math.round(v)}`;
};

export const formatNumber = (n) => (Number(n) || 0).toLocaleString('en-IN');

// Measures an element's width so charts can draw at real pixel size (crisp text on every screen).
function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return [ref, width];
}

// "Nice" axis maximum and ticks for a value range.
function niceScale(max, tickCount = 4) {
  if (max <= 0) return { max: 1, ticks: [0, 1] };
  const rough = max / tickCount;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / pow;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * pow;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) ticks.push(v);
  return { max: niceMax, ticks };
}

const AXIS = '#8a97a6';
const GRID = '#edf1f5';

// ---------- Vertical bar chart ----------
export function BarChart({ data, color = '#137173', height = 270, formatValue = formatNumber, valueLabel = '' }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { top: 14, right: 8, bottom: 28, left: 44 };
  const innerW = Math.max(width - pad.left - pad.right, 10);
  const innerH = height - pad.top - pad.bottom;
  const { max, ticks } = niceScale(Math.max(...data.map((d) => d.value), 0));
  const slot = innerW / Math.max(data.length, 1);
  const barW = Math.min(slot * 0.62, 34);
  const y = (v) => pad.top + innerH - (v / max) * innerH;
  const last = data.length - 1;
  return (
    <div ref={ref} className="dash-chart" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Bar chart">
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke={GRID} />
              <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={AXIS}>{formatCompactTick(t)}</text>
            </g>
          ))}
          {data.map((d, i) => {
            const cx = pad.left + slot * i + slot / 2;
            const h = Math.max((d.value / max) * innerH, d.value > 0 ? 2 : 0);
            return (
              <g key={d.key} onMouseEnter={() => setHover({ i, cx })} onMouseLeave={() => setHover(null)}>
                <rect x={pad.left + slot * i} y={pad.top} width={slot} height={innerH} fill="transparent" />
                <rect
                  x={cx - barW / 2} y={pad.top + innerH - h} width={barW} height={h} rx={5}
                  fill={color} opacity={hover && hover.i !== i ? 0.45 : i === last ? 1 : 0.82}
                />
                <text x={cx} y={height - 8} textAnchor="middle" fontSize="11" fill={hover && hover.i === i ? '#1c2733' : AXIS} fontWeight={hover && hover.i === i ? 600 : 400}>{d.label}</text>
              </g>
            );
          })}
        </svg>
      )}
      {hover && data[hover.i] && (
        <div className="dash-tip" style={{ left: Math.min(Math.max(hover.cx, 70), Math.max(width - 70, 70)), top: Math.max(y(data[hover.i].value) - 52, 0) }}>
          <strong>{formatValue(data[hover.i].value)}</strong> {valueLabel}
          <span>{data[hover.i].label} {data[hover.i].year}</span>
        </div>
      )}
    </div>
  );
}

function formatCompactTick(v) {
  if (v >= 1e7) return `${v / 1e7}Cr`;
  if (v >= 1e5) return `${v / 1e5}L`;
  if (v >= 1e3) return `${v / 1e3}K`;
  return String(v);
}

// ---------- Area / line chart ----------
export function AreaChart({ data, color = '#137173', height = 270, formatValue = formatNumber, formatTick = formatCompactTick, valueLabel = '' }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const gradId = useRef(`g${Math.random().toString(36).slice(2, 8)}`).current;
  const pad = { top: 14, right: 14, bottom: 28, left: 44 };
  const innerW = Math.max(width - pad.left - pad.right, 10);
  const innerH = height - pad.top - pad.bottom;
  const { max, ticks } = niceScale(Math.max(...data.map((d) => d.value), 0));
  const step = data.length > 1 ? innerW / (data.length - 1) : innerW;
  const x = (i) => pad.left + step * i;
  const y = (v) => pad.top + innerH - (v / max) * innerH;
  const pts = data.map((d, i) => [x(i), y(d.value)]);
  // smooth curve (monotone-ish via cardinal-style control points)
  const line = pts.map((p, i) => {
    if (i === 0) return `M${p[0]},${p[1]}`;
    const prev = pts[i - 1];
    const cx = (prev[0] + p[0]) / 2;
    return `C${cx},${prev[1]} ${cx},${p[1]} ${p[0]},${p[1]}`;
  }).join(' ');
  const area = `${line} L${x(data.length - 1)},${pad.top + innerH} L${x(0)},${pad.top + innerH} Z`;
  return (
    <div ref={ref} className="dash-chart" style={{ height }}>
      {width > 0 && (
        <svg
          width={width} height={height} role="img" aria-label="Line chart"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const i = Math.round((e.clientX - r.left - pad.left) / step);
            setHover(i >= 0 && i < data.length ? i : null);
          }}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke={GRID} />
              <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={AXIS}>{formatTick(t)}</text>
            </g>
          ))}
          <path d={area} fill={`url(#${gradId})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {data.map((d, i) => (
            <text key={d.key} x={x(i)} y={height - 8} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'} fontSize="11" fill={hover === i ? '#1c2733' : AXIS} fontWeight={hover === i ? 600 : 400}>{d.label}</text>
          ))}
          {hover !== null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} stroke="#cfd8e1" strokeDasharray="3 3" />
              <circle cx={x(hover)} cy={y(data[hover].value)} r="5" fill="#fff" stroke={color} strokeWidth="2.5" />
            </g>
          )}
          {hover === null && <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="4" fill={color} />}
        </svg>
      )}
      {hover !== null && (
        <div className="dash-tip" style={{ left: Math.min(Math.max(x(hover), 70), Math.max(width - 70, 70)), top: Math.max(y(data[hover].value) - 56, 0) }}>
          <strong>{formatValue(data[hover].value)}</strong> {valueLabel}
          <span>{data[hover].label} {data[hover].year}</span>
        </div>
      )}
    </div>
  );
}

// ---------- Tiny trend line for KPI cards ----------
export function Sparkline({ values, color = '#137173', width = 92, height = 34 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => [i * step, height - 3 - ((v - min) / span) * (height - 8)]);
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const lastPt = pts[pts.length - 1];
  return (
    <svg width={width} height={height} aria-hidden="true" className="dash-spark">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.8" fill={color} />
    </svg>
  );
}

// ---------- Horizontal ranked bars (students by course) ----------
export function HBarList({ items, color = '#137173', emptyText = 'No data yet' }) {
  if (!items || items.length === 0) return <div className="dash-empty">{emptyText}</div>;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="dash-hbars">
      {items.map((it) => (
        <li key={it.id ?? it.name} title={`${it.name}: ${it.value}`}>
          <div className="dash-hbars-top">
            <span className="dash-hbars-name">{it.name}</span>
            <span className="dash-hbars-val">{formatNumber(it.value)}</span>
          </div>
          <div className="dash-hbars-track"><div className="dash-hbars-fill" style={{ width: `${(it.value / max) * 100}%`, background: color }} /></div>
        </li>
      ))}
    </ul>
  );
}

// ---------- Donut (payment methods) ----------
export const DONUT_COLORS = ['#137173', '#d4a24c', '#3b6fd6', '#7a5af8', '#f0883e', '#8a97a6'];

export function Donut({ items, formatValue = formatINRCompact, centerLabel = 'Total', size = 168 }) {
  const total = items.reduce((s, i) => s + i.value, 0);
  if (!items.length || total <= 0) return <div className="dash-empty">No payments in this period</div>;
  const r = size / 2 - 14;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="dash-donut">
      <div className="dash-donut-ring" style={{ width: size, height: size }}>
        <svg width={size} height={size} role="img" aria-label="Payment methods">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#edf1f5" strokeWidth="20" />
          {items.map((it, i) => {
            const len = (it.value / total) * c;
            const seg = (
              <circle
                key={it.name} cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth="20"
                strokeDasharray={`${Math.max(len - 2, 0)} ${c - Math.max(len - 2, 0)}`}
                strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += len;
            return seg;
          })}
        </svg>
        <div className="dash-donut-center"><strong>{formatValue(total)}</strong><span>{centerLabel}</span></div>
      </div>
      <ul className="dash-legend">
        {items.map((it, i) => (
          <li key={it.name}>
            <i style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="dash-legend-name">{it.name}</span>
            <span className="dash-legend-val">{formatValue(it.value)} <em>{Math.round((it.value / total) * 100)}%</em></span>
          </li>
        ))}
      </ul>
    </div>
  );
}
